// Relative-time formatter shared across the SPA. Pulls phrases from the active locale's
// `catalogTime.*` keys so "today / 2d ago / 3w ago" become "hoje / há 2d / há 3sem" in PT-BR.
// Granularity caps at "years ago" - anything older renders with the same year suffix.
//
// Usage:
//   import { useTranslation } from '../i18n';
//   import { formatRelative } from '../utils/relativeTime';
//   const t = useTranslation();
//   const label = formatRelative(pkg.lastChangedAt, t);
//
// The `t` parameter is the translation function from useTranslation(); we accept it as an
// argument rather than calling useTranslation() inside so this stays a pure function (no
// component dependency) and can be used outside of render trees too.

export type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

export function formatRelative(iso: string | null | undefined, t: TranslateFn): string {
    if (!iso) return '';
    const ts = Date.parse(iso);
    if (Number.isNaN(ts)) return '';
    const days = Math.floor((Date.now() - ts) / 86_400_000);
    if (days < 1) return t('catalogTime.today');
    if (days < 7) return t('catalogTime.daysAgo', { n: days });
    if (days < 30) return t('catalogTime.weeksAgo', { n: Math.floor(days / 7) });
    if (days < 365) return t('catalogTime.monthsAgo', { n: Math.floor(days / 30) });
    return t('catalogTime.yearsAgo', { n: Math.floor(days / 365) });
}
